import React, { useEffect, useState } from "react";
import { CardContainer } from "../../../components/Card/CardContainer";
import {
  Badge,
  Card,
  Col,
  ConfigProvider,
  Progress,
  ProgressProps,
  Row,
  Space,
  Table,
  Tabs,
  Tag,
  Typography,
  DatePicker,
} from "antd";
import Text from "../../../components/Text/Text";
import Button from "../../../components/Button/Button";
import { useNavigate, useSearchParams } from "react-router-dom";
import { icon, image } from "../../../resource";
import { FlexCol } from "../../../components/Container/Container";
import Input from "../../../components/Input/Input";
import { SearchOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { getCoDiscoutType, getUseageSummary } from "../../../datasource/CODiscountDatasource";
import Permission from "../../../components/Permission/Permission";
import { ActionBtn } from "../../../components/Button/TableActionButton";
import { PieChart, Pie, Cell } from "recharts";
import { useEffectOnce } from "react-use";
import "./../CODiscountClass.css";
import dayjs from "dayjs";
import { useTabState } from "../../../hook/useTabState";
import SortFieldCustom from "../../../components/SortFieldTable/SortFieldCustom";
import { numberFormatter } from "../../../utility/Formatter";
import { FixedType } from "rc-table/lib/interface";

export const IndexCoDiscountType: React.FC = () => {
  const profile = JSON.parse(localStorage.getItem("profile")!);
  const navigate = useNavigate();
  const orderTabState = useTabState();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<any>([]);
  const [dataPie, setDataPie] = useState<any>([]);
  const [dataProgress, setDataProgress] = useState<any>([]);
  const [status, setStatus] = useState<any>(searchParams.get("status") || "All");
  const [searchText, setSearchText] = useState<any>(searchParams.get("searchText") || undefined);
  const [searchDate, setSearchDate] = useState<any>([null, null]);
  const [page, setPage] = useState<any>(1);
  const [take, setTake] = useState(10);

  const fetchData = async () => {
    await getCoDiscoutType({
      company: profile?.company,
      status: status === "All" ? undefined : status,
      searchText: searchText || undefined,
      page: page,
      take: take,
      sortField: orderTabState.sortDirection ? orderTabState.sortField : undefined,
      sortDirection: orderTabState.sortDirection,
    }).then((res) => {
      setData(res);
    });
  };

  const fetchDataSummary = async (setState: any) => {
    await getUseageSummary({
      company: profile?.company,
      startDate: searchDate[0] ? searchDate[0] : undefined,
      endDate: searchDate[1] ? searchDate[1] : undefined,
    }).then((res) => {
      setState(res);
    });
  };

  useEffectOnce(() => {
    fetchDataSummary(setDataPie);
  });

  useEffect(() => {
    fetchData();
  }, [status, searchText, page, take, orderTabState.sortField, orderTabState.sortDirection]);

  useEffect(() => {
    fetchDataSummary(setDataProgress);
  }, [searchDate]);

  const setParams = (params?: { searchText?: string; status?: string; page?: string }) => {
    const nextSearchText = params?.searchText ?? searchText;
    const nextStatus = params?.status ?? status;
    const nextPage = params?.page ?? page;

    setSearchParams({
      searchText: nextSearchText || "",
      status: nextStatus || "",
      page: nextPage,
    });
  };

  const PieChartComponent: React.FC<ProgressProps> = () => {
    const size = 255;
    const sortedData = dataPie?.sort((a: any, b: any) => b.coCount - a.coCount).slice(0, 10);
    return (
      <Card>
        <div
          style={{
            minHeight: size,
          }}
        >
          <Col>
            <Text fontWeight={600}>10 อันดับประเภทใช้สูงที่สุด</Text>
          </Col>
          <Row justify={"space-around"} align='middle' gutter={8}>
            <Col>
              <div style={{ position: "relative", width: size, height: size }}>
                <PieChart width={size} height={size}>
                  {sortedData?.some((item) => item.coCount > 0) ? (
                    <Pie
                      data={sortedData}
                      dataKey='coCount'
                      innerRadius={size * 0.25}
                      outerRadius={size * 0.4}
                      paddingAngle={0}
                    >
                      {sortedData?.map((entry, index) => {
                        return <Cell key={index} fill={entry.color} />;
                      })}
                    </Pie>
                  ) : (
                    <Pie
                      data={[{ value: 1 }]}
                      dataKey='value'
                      innerRadius={size * 0.25}
                      outerRadius={size * 0.4}
                      isAnimationActive={false}
                      fill='#F0F0F0'
                      stroke='none'
                    />
                  )}
                </PieChart>
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    pointerEvents: "none",
                  }}
                >
                  <Text fontSize={50} fontWeight={600}>
                    {sortedData?.length || 0}
                  </Text>
                  <Text color='Text3'>ประเภท</Text>
                </div>
              </div>
            </Col>
            <Col>
              {sortedData?.length > 0 ? (
                <Row gutter={24}>
                  <Col>
                    <Space direction='vertical' size={12}>
                      {sortedData?.slice(0, Math.ceil(sortedData?.length / 2)).map((item) => (
                        <Badge
                          key={item.code}
                          color={item.color}
                          text={
                            <>
                              <Typography.Text style={{ color: item.color, fontWeight: 800 }}>
                                {item.code}
                              </Typography.Text>{" "}
                              <Text color='Text3' level={6}>
                                ({item.coCount})
                              </Text>
                            </>
                          }
                          className='custom-badge'
                        />
                      ))}
                    </Space>
                  </Col>
                  <Col>
                    <Space direction='vertical' size={12}>
                      {sortedData?.slice(Math.ceil(sortedData?.length / 2)).map((item) => (
                        <Badge
                          key={item.code}
                          color={item.color}
                          text={
                            <>
                              <Typography.Text style={{ color: item.color, fontWeight: 800 }}>
                                {item.code}
                              </Typography.Text>{" "}
                              <Text color='Text3' level={6}>
                                ({item.coCount})
                              </Text>
                            </>
                          }
                          className='custom-badge'
                        />
                      ))}
                    </Space>
                  </Col>
                </Row>
              ) : (
                <Text>ไม่มีประเภทส่วนลด</Text>
              )}
            </Col>
          </Row>
        </div>
      </Card>
    );
  };

  const ProgressComponent: React.FC<ProgressProps> = () => {
    const sortedData = dataProgress
      ?.sort((a: any, b: any) => b.usedAmount - a.usedAmount || b.totalAmount - a.totalAmount)
      .slice(0, 5);
    return (
      <Card>
        <div
          style={{
            minHeight: 280,
          }}
        >
          <Row justify={"space-between"}>
            <Col>
              <Text fontWeight={600}>5 อันดับยอดใช้ส่วนลดสูงสุดตามประเภท</Text>
            </Col>
            <Col span={10}>
              <DatePicker.RangePicker
                format='DD/MM/YYYY'
                allowClear
                allowEmpty={[true, true]}
                disabledDate={(current: any) => {
                  return current && current.isAfter(dayjs().endOf("day"));
                }}
                style={{ height: "30px" }}
                value={[
                  searchDate[0] ? dayjs(searchDate[0]) : null,
                  searchDate[1] ? dayjs(searchDate[1]) : null,
                ]}
                onChange={(dates) => {
                  if (dates && dates[0] && dates[1]) {
                    setSearchDate([
                      dayjs(dates[0])?.format("YYYY-MM-DD"),
                      dayjs(dates[1])?.format("YYYY-MM-DD"),
                    ]);
                  } else {
                    setSearchDate([undefined, undefined]);
                  }
                }}
              />
            </Col>
          </Row>
          <br />
          {sortedData?.length > 0 ? (
            sortedData?.slice(0, 5).map((item, index) => (
              <React.Fragment key={index}>
                <Row justify={"space-between"} align='middle' gutter={8}>
                  <Col>
                    <FlexCol>
                      <Tag style={{ backgroundColor: `${item?.color}20`, border: "none" }}>
                        <Typography.Text
                          style={{
                            fontFamily: "IBM Plex Sans Thai",
                            color: item?.color,
                            fontWeight: "bold",
                            fontSize: 15,
                          }}
                        >
                          {item.code} • {item?.nameTh}
                        </Typography.Text>
                      </Tag>
                    </FlexCol>
                  </Col>
                  {item.totalAmount > 0 && (
                    <Col>
                      <Text level={6}>{numberFormatter(item.usedAmount)} ฿</Text>
                      {" / "}
                      <Text color='Text3' level={6}>
                        {numberFormatter(item.totalAmount)} ฿
                      </Text>
                    </Col>
                  )}
                </Row>
                <Progress
                  key={index}
                  strokeColor={item.color}
                  percent={item.totalAmount ? (item.usedAmount / item.totalAmount) * 100 : 0}
                  showInfo={false}
                />
              </React.Fragment>
            ))
          ) : (
            <div
              style={{
                padding: 32,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 24,
                minHeight: 100,
              }}
            >
              <img
                src={icon.emptyHistoryPoint}
                style={{
                  width: 80,
                  height: 90,
                  alignItems: "center",
                }}
                alt='empty_table'
              />

              <Text
                fontFamily='Sarabun'
                fontSize={16}
                fontWeight={400}
                color='Text3'
                align='center'
              >
                ไม่มีประเภทส่วนลด
              </Text>
            </div>
          )}
        </div>
      </Card>
    );
  };

  const columes = [
    {
      title: (
        <SortFieldCustom
          title='รหัส'
          sortDirection={
            orderTabState.sortField === "code" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("code")}
        />
      ),
      dataIndex: "code",
      key: "code",
      width: "8%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='ชื่อประเภท'
          sortDirection={
            orderTabState.sortField === "nameTh" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("nameTh")}
        />
      ),
      dataIndex: "nameTh",
      key: "nameTh",
      width: "30%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value}</Text>
              <Text color='Text3' level={6}>
                {row?.nameEn}
              </Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='รายละเอียด'
          sortDirection={
            orderTabState.sortField === "description" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("description")}
        />
      ),
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Text>{value?.length > 95 ? `${value?.slice(0, 95)}...` : value || "-"}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='กำลังใช้ (รายการ)'
          sortDirection={
            orderTabState.sortField === "coCount" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("coCount")}
        />
      ),
      dataIndex: "coCount",
      key: "coCount",
      width: "15%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol align={"center"}>
              <Text>{value}</Text>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='แสดงผล'
          sortDirection={
            orderTabState.sortField === "code" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("code")}
        />
      ),
      dataIndex: "render",
      key: "render",
      width: "25%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Tag style={{ backgroundColor: `${row?.color}20`, border: "none" }}>
                <Typography.Text
                  style={{
                    fontFamily: "IBM Plex Sans Thai",
                    color: row?.color,
                    fontWeight: "bold",
                    fontSize: 16,
                  }}
                >
                  {row.code} • {row?.nameTh}
                </Typography.Text>
              </Tag>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: (
        <SortFieldCustom
          title='สถานะ'
          sortDirection={
            orderTabState.sortField === "status" ? orderTabState.sortDirection : undefined
          }
          onSortChange={() => orderTabState.handleSortChange("status")}
        />
      ),
      width: "10%",
      dataIndex: "status",
      key: "status",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <FlexCol>
              <Tag style={{ backgroundColor: value ? "#E2FDEB" : "#F3F6F9", border: "none" }}>
                <Text color={value ? "EmeraldGreen" : "Disable"} level={6} fontWeight={600}>
                  {value ? "เปิดใช้งาน" : "ปิดใช้งาน"}
                </Text>
              </Tag>
            </FlexCol>
          ),
        };
      },
    },
    {
      title: "จัดการ",
      dataIndex: "coStatus",
      key: "coStatus",
      fixed: "right" as FixedType | undefined,
      width: "10%",
      render: (value: any, row: any, index: number) => {
        return {
          children: (
            <Row>
              <Permission permission={["coDiscountType", "view"]}>
                <ActionBtn
                  onClick={() =>
                    navigate(
                      "/newDiscountCo/detailDiscountType/" + row.creditMemoTypeId + "/coType",
                    )
                  }
                  icon={<UnorderedListOutlined />}
                />
              </Permission>
            </Row>
          ),
        };
      },
    },
  ];

  return (
    <>
      <CardContainer>
        <Row justify={"space-between"}>
          <Col>
            <div>
              <span
                className='card-label font-weight-bolder text-dark'
                style={{ fontSize: 20, fontWeight: "bold" }}
              >
                ประเภทส่วนลด CO
              </span>
            </div>
          </Col>
          <Col>
            <Permission permission={["coDiscountType", "create"]}>
              <Button
                type='primary'
                title='+ สร้างประเภทส่วนลด'
                height={40}
                onClick={() => navigate("/newDiscountCo/coDiscountType/0")}
                size='large'
              />
            </Permission>
          </Col>
        </Row>
        <br />
        <Row justify={"space-between"} gutter={8}>
          <Col span={12}>
            <PieChartComponent />
          </Col>
          <Col span={12}>
            <ProgressComponent />
          </Col>
        </Row>
        <br />
        <Tabs
          activeKey={status}
          onChange={(key) => {
            setStatus(key);
            setParams({ status: key, page: "1" });
          }}
          items={[
            {
              label: `ทั้งหมด (${data?.countStatus?.["all"] || 0})`,
              key: "All",
            },
            {
              label: `เปิดใช้งาน (${data?.countStatus?.["active"] || 0})`,
              key: "true",
            },
            {
              label: `ปิดใช้งาน (${data?.countStatus?.["inactive"] || 0})`,
              key: "false",
            },
          ]}
        />
        <div style={{ paddingBottom: "8px" }}>
          <Input
            allowClear
            value={searchText}
            placeholder='ค้นหาประเภทส่วนลด'
            suffix={<SearchOutlined />}
            onPressEnter={(e: any) => {
              setSearchText(e.target.value);
              setParams({ searchText: e.target.value, page: "1" });
            }}
            onChange={(e) => {
              if (!e.target.value) {
                setSearchText(undefined);
                setParams({ searchText: "", page: "1" });
              }
            }}
          />
        </div>
        <ConfigProvider
          theme={{
            components: {
              Table: {
                headerBg: "#F3F6F9",
                fontFamily: "IBM Plex Sans Thai",
                fontSize: 15,
              },
            },
          }}
        >
          <Table
            columns={columes}
            dataSource={data?.data}
            scroll={{
              x: 1680,
            }}
            pagination={{
              position: ["bottomCenter"],
              total: data?.count,
              pageSize: take,
              current: page,
              onChange: (page, pageSize) => {
                setPage(page);
                setTake(pageSize);
              },
            }}
            locale={{
              emptyText: () => (
                <div
                  style={{
                    height: "300px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Col>
                    <img src={image.emptyTableBrand} alt='empty_table' />
                  </Col>
                  <Col>
                    <div style={{ fontSize: "16px" }}>ไม่มีประเภทส่วนลด</div>
                  </Col>
                </div>
              ),
            }}
          />
        </ConfigProvider>
      </CardContainer>
    </>
  );
};
